/**
 * React Query hooks for identity variants (LF-5566 PROV-21).
 *
 * All calls go through directApiClient. Cache invalidation resolves through the
 * centralized `getDependencies('identity-variant', ownerId, ownerType)` map — no
 * hand-rolled invalidation list (see `lib/services/query-dependencies.ts`).
 */

'use client';

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions
} from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { identityVariantKeys } from '@/lib/query-keys';
import { getDependencies } from '@/lib/services/query-dependencies';
import { FRESH_DATA_CONFIG } from '../query-config';
import {
  createAddressVariant,
  createAssetIdentityReference,
  createNameVariant,
  deleteAddressVariant,
  deleteAssetIdentityReference,
  deleteNameVariant,
  getIdentityVariants,
  updateAddressVariant,
  updateAssetIdentityReference,
  updateNameVariant
} from './client';
import type {
  AddressVariant,
  AssetIdentityReference,
  CreateAddressVariantRequest,
  CreateAssetIdentityReferenceRequest,
  CreateNameVariantRequest,
  IdentityVariantOwnerType,
  IdentityVariantsResponse,
  NameVariant,
  UpdateAddressVariantRequest,
  UpdateAssetIdentityReferenceRequest,
  UpdateNameVariantRequest
} from './types';

interface OwnerArgs {
  ownerType: IdentityVariantOwnerType;
  ownerId: number | undefined;
}

/**
 * Every identity variant owned by one person, in one request.
 *
 * Disabled until an owner id exists — the "add contact" flow renders the form
 * before the contact is persisted, and there is nothing to fetch (or attach a
 * variant to) until it is.
 */
export function useIdentityVariants(
  { ownerType, ownerId }: OwnerArgs,
  options?: Omit<
    UseQueryOptions<IdentityVariantsResponse>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: identityVariantKeys.forOwner(ownerType, ownerId ?? 0),
    queryFn: () => getIdentityVariants(ownerType, ownerId as number),
    ...FRESH_DATA_CONFIG,
    enabled: !!ownerId,
    ...options
  });
}

/** Shared invalidation for every variant mutation on one owner. */
function useInvalidateOwner({ ownerType, ownerId }: OwnerArgs) {
  const queryClient = useQueryClient();
  return async () => {
    if (!ownerId) return;
    const keys = getDependencies('identity-variant', ownerId, ownerType);
    await Promise.all(
      keys.map((queryKey) => queryClient.invalidateQueries({ queryKey }))
    );
  };
}

/**
 * **Suppresses the success toast, and only that** (VALE-863).
 *
 * The attribute treatment reports a save inline — a saving indicator on the row
 * and a refusal ring when it is rejected — and deliberately never toasts, so a
 * reader editing several fields in a drawer is not stacking notifications for
 * work they can already see landed. The identity-variant rows render on that
 * treatment now, so they need the same silence.
 *
 * **`onError` is untouched.** A failed save has no inline surface on a `render`
 * row, so silencing it would drop the only signal that anything went wrong.
 * That asymmetry is the point of the name: it suppresses the confirmation, not
 * the failure.
 *
 * The legacy subsections (`OtherNamesSubsection` and friends, still mounted by
 * the Edit-matter and Edit-contact dialogs) pass nothing and keep their toasts,
 * which is right — they have no inline reporting of their own.
 */
type SilentSuccess = { silentSuccess?: boolean };

// ── Name variants ────────────────────────────────────────────────────────────

export function useCreateNameVariant(
  owner: OwnerArgs,
  options?: UseMutationOptions<NameVariant, Error, CreateNameVariantRequest> &
    SilentSuccess
) {
  const invalidate = useInvalidateOwner(owner);

  return useMutation({
    mutationFn: (data: CreateNameVariantRequest) =>
      createNameVariant(owner.ownerType, owner.ownerId as number, data),
    onSuccess: async (data, variables, onMutateResult, context) => {
      await invalidate();
      if (!options?.silentSuccess) toast.success('Name added');
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast.error(error.message || 'Failed to add the name');
      options?.onError?.(error, variables, onMutateResult, context);
    },
    ...options
  });
}

export function useUpdateNameVariant(
  owner: OwnerArgs,
  options?: UseMutationOptions<
    NameVariant,
    Error,
    { id: number; data: UpdateNameVariantRequest }
  > &
    SilentSuccess
) {
  const invalidate = useInvalidateOwner(owner);

  return useMutation({
    mutationFn: ({ id, data }) => updateNameVariant(id, data),
    onSuccess: async (data, variables, onMutateResult, context) => {
      await invalidate();
      if (!options?.silentSuccess) toast.success('Name updated');
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast.error(error.message || 'Failed to update the name');
      options?.onError?.(error, variables, onMutateResult, context);
    },
    ...options
  });
}

export function useDeleteNameVariant(
  owner: OwnerArgs,
  options?: UseMutationOptions<void, Error, number> & SilentSuccess
) {
  const invalidate = useInvalidateOwner(owner);

  return useMutation({
    mutationFn: (id: number) => deleteNameVariant(id),
    onSuccess: async (data, variables, onMutateResult, context) => {
      await invalidate();
      if (!options?.silentSuccess) toast.success('Name removed');
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast.error(error.message || 'Failed to remove the name');
      options?.onError?.(error, variables, onMutateResult, context);
    },
    ...options
  });
}

// ── Address variants ─────────────────────────────────────────────────────────

export function useCreateAddressVariant(
  owner: OwnerArgs,
  options?: UseMutationOptions<
    AddressVariant,
    Error,
    CreateAddressVariantRequest
  > &
    SilentSuccess
) {
  const invalidate = useInvalidateOwner(owner);

  return useMutation({
    mutationFn: (data: CreateAddressVariantRequest) =>
      createAddressVariant(owner.ownerType, owner.ownerId as number, data),
    onSuccess: async (data, variables, onMutateResult, context) => {
      await invalidate();
      if (!options?.silentSuccess) toast.success('Address added');
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast.error(error.message || 'Failed to add the address');
      options?.onError?.(error, variables, onMutateResult, context);
    },
    ...options
  });
}

export function useUpdateAddressVariant(
  owner: OwnerArgs,
  options?: UseMutationOptions<
    AddressVariant,
    Error,
    { id: number; data: UpdateAddressVariantRequest }
  > &
    SilentSuccess
) {
  const invalidate = useInvalidateOwner(owner);

  return useMutation({
    mutationFn: ({ id, data }) => updateAddressVariant(id, data),
    onSuccess: async (data, variables, onMutateResult, context) => {
      await invalidate();
      if (!options?.silentSuccess) toast.success('Address updated');
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast.error(error.message || 'Failed to update the address');
      options?.onError?.(error, variables, onMutateResult, context);
    },
    ...options
  });
}

export function useDeleteAddressVariant(
  owner: OwnerArgs,
  options?: UseMutationOptions<void, Error, number> & SilentSuccess
) {
  const invalidate = useInvalidateOwner(owner);

  return useMutation({
    mutationFn: (id: number) => deleteAddressVariant(id),
    onSuccess: async (data, variables, onMutateResult, context) => {
      await invalidate();
      if (!options?.silentSuccess) toast.success('Address removed');
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast.error(error.message || 'Failed to remove the address');
      options?.onError?.(error, variables, onMutateResult, context);
    },
    ...options
  });
}

// ── Asset-identity references ────────────────────────────────────────────────

export function useCreateAssetIdentityReference(
  owner: OwnerArgs,
  options?: UseMutationOptions<
    AssetIdentityReference,
    Error,
    CreateAssetIdentityReferenceRequest
  > &
    SilentSuccess
) {
  const invalidate = useInvalidateOwner(owner);

  return useMutation({
    mutationFn: (data: CreateAssetIdentityReferenceRequest) =>
      createAssetIdentityReference(
        owner.ownerType,
        owner.ownerId as number,
        data
      ),
    onSuccess: async (data, variables, onMutateResult, context) => {
      await invalidate();
      if (!options?.silentSuccess) toast.success('Holding reference added');
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast.error(error.message || 'Failed to add the holding reference');
      options?.onError?.(error, variables, onMutateResult, context);
    },
    ...options
  });
}

export function useUpdateAssetIdentityReference(
  owner: OwnerArgs,
  options?: UseMutationOptions<
    AssetIdentityReference,
    Error,
    { id: number; data: UpdateAssetIdentityReferenceRequest }
  > &
    SilentSuccess
) {
  const invalidate = useInvalidateOwner(owner);

  return useMutation({
    mutationFn: ({ id, data }) => updateAssetIdentityReference(id, data),
    onSuccess: async (data, variables, onMutateResult, context) => {
      await invalidate();
      if (!options?.silentSuccess) toast.success('Holding reference updated');
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast.error(error.message || 'Failed to update the holding reference');
      options?.onError?.(error, variables, onMutateResult, context);
    },
    ...options
  });
}

export function useDeleteAssetIdentityReference(
  owner: OwnerArgs,
  options?: UseMutationOptions<void, Error, number> & SilentSuccess
) {
  const invalidate = useInvalidateOwner(owner);

  return useMutation({
    mutationFn: (id: number) => deleteAssetIdentityReference(id),
    onSuccess: async (data, variables, onMutateResult, context) => {
      await invalidate();
      if (!options?.silentSuccess) toast.success('Holding reference removed');
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast.error(error.message || 'Failed to remove the holding reference');
      options?.onError?.(error, variables, onMutateResult, context);
    },
    ...options
  });
}
